import React, { useRef, useState } from 'react';
import { Typography, Avatar, Chip, Box, Button, IconButton, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import * as S from './style';
import { ColorsEnum } from 'common/theme/colors';
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGE_FILE_SIZE_BYTES, fileToResizedDataUrl } from 'common/utils/image';

const Profile: React.FC = () => {
  const { user, updateAvatar } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoError, setPhotoError] = useState('');
  const [photoSaving, setPhotoSaving] = useState(false);

  if (!user) {
    return null;
  }

  const handlePhotoButtonClick = () => {
    setPhotoError('');
    fileInputRef.current?.click();
  };

  const handlePhotoSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setPhotoError('Please choose a PNG, JPEG, WEBP, or GIF image.');
      return;
    }
    if (file.size > MAX_IMAGE_FILE_SIZE_BYTES) {
      setPhotoError('Image is too large. Please choose a file under 5MB.');
      return;
    }

    setPhotoSaving(true);
    try {
      const dataUrl = await fileToResizedDataUrl(file);
      await updateAvatar(dataUrl);
      setPhotoError('');
    } catch (err) {
      setPhotoError('Failed to save photo. Please try again.');
    } finally {
      setPhotoSaving(false);
    }
  };

  const initials = user.full_name
    ? user.full_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2)
    : user.username.substring(0, 2).toUpperCase();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <S.PageContainer>
      <S.ContentWrapper>
        <S.Header>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 2,
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            Back
          </Button>
          <Typography variant="h5" fontWeight={700}>
            My Profile
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            View and manage your account information
          </Typography>
        </S.Header>

        <S.ProfileCard>
          <S.AvatarSection>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar
                src={user.avatar_url ?? undefined}
                sx={{
                  width: 72,
                  height: 72,
                  backgroundColor: ColorsEnum.darkGrey,
                  fontSize: '1.75rem',
                  fontWeight: 600,
                  opacity: photoSaving ? 0.6 : 1,
                }}
              >
                {initials}
              </Avatar>
              <IconButton
                size="small"
                onClick={handlePhotoButtonClick}
                disabled={photoSaving}
                aria-label="Edit profile photo"
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  width: 26,
                  height: 26,
                  backgroundColor: ColorsEnum.darkGrey,
                  color: ColorsEnum.white,
                  '&:hover': { backgroundColor: ColorsEnum.darkGrey, opacity: 0.9 },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(',')}
                hidden
                onChange={handlePhotoSelected}
              />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h5" fontWeight={600}>
                  {user.full_name || user.username}
                </Typography>
                {user.role === 'superadmin' ? (
                  <Chip
                    label="Admin"
                    size="small"
                    sx={{
                      backgroundColor: ColorsEnum.darkGrey,
                      color: ColorsEnum.white,
                      fontWeight: 600,
                    }}
                  />
                ) : (
                  <Chip
                    label="Demo"
                    size="small"
                    sx={{
                      backgroundColor: ColorsEnum.secondary,
                      color: ColorsEnum.white,
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
          </S.AvatarSection>

          {photoError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setPhotoError('')}>
              {photoError}
            </Alert>
          )}

          <S.InfoRow>
            <S.InfoLabel>User ID</S.InfoLabel>
            <S.InfoValue>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {user.user_id}
              </Typography>
            </S.InfoValue>
          </S.InfoRow>

          <S.InfoRow>
            <S.InfoLabel>Username</S.InfoLabel>
            <S.InfoValue>
              <Typography variant="body2">{user.username}</Typography>
            </S.InfoValue>
          </S.InfoRow>

          <S.InfoRow>
            <S.InfoLabel>Email Address</S.InfoLabel>
            <S.InfoValue>
              <Typography variant="body2">{user.email}</Typography>
            </S.InfoValue>
          </S.InfoRow>

          <S.InfoRow>
            <S.InfoLabel>Full Name</S.InfoLabel>
            <S.InfoValue>
              <Typography variant="body2">{user.full_name || 'Not set'}</Typography>
            </S.InfoValue>
          </S.InfoRow>

          <S.InfoRow>
            <S.InfoLabel>Role</S.InfoLabel>
            <S.InfoValue>
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                {user.role.replace('_', ' ')}
              </Typography>
            </S.InfoValue>
          </S.InfoRow>

          <S.InfoRow>
            <S.InfoLabel>Account Status</S.InfoLabel>
            <S.InfoValue>
              <Chip
                label={user.is_active ? 'Active' : 'Inactive'}
                size="small"
                color={user.is_active ? 'success' : 'default'}
              />
            </S.InfoValue>
          </S.InfoRow>

          <S.InfoRow>
            <S.InfoLabel>Member Since</S.InfoLabel>
            <S.InfoValue>
              <Typography variant="body2">{formatDate(user.created_at)}</Typography>
            </S.InfoValue>
          </S.InfoRow>
        </S.ProfileCard>
      </S.ContentWrapper>
    </S.PageContainer>
  );
};

export default Profile;
